import React, { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "../../redux/store"
import { getEvent } from "../../redux/slices/events/eventsSlice"
import { useBackgroundRefresh } from "./hooks/useBackgroundRefresh"
import EditGuestDrawer from "./components/EditGuestDrawer"
import GuestDetailDrawer from "./components/GuestDetailDrawer"
import AddGuestsDrawer from "./components/AddGuestsDrawer"
import EventBanner from "./components/EventBanner"
import EventHeader from "./components/EventHeader"
import Overview from "./components/Overview"
import GuestList, { type StatusFilter } from "./components/GuestList"
import RemindersOverview from "./components/RemindersOverview"
import RemindersGuestList, {
  type InvitationStatusFilter,
  type ReminderStatusFilter,
} from "./components/RemindersGuestList"
import ThankYouOverview, {
  type ThankStatusFilter,
} from "./components/ThankYouOverview"
import ThankYouGuestList from "./components/ThankYouGuestList"
import styles from "./EventDashboardV2.module.scss"

const TABS = [
  { id: "invitation", labelKey: "invitation" },
  { id: "reminders", labelKey: "reminders" },
  { id: "rsvp-reminder", labelKey: "rsvpReminder" },
  { id: "qr-reminder", labelKey: "qrCodeReminder" },
  { id: "thank-you", labelKey: "thankYouMessages" },
] as const

type TabId = (typeof TABS)[number]["id"]

const EventDashboardV2: React.FC = () => {
  const { event_id } = useParams()
  const dispatch = useAppDispatch()
  const { t, i18n } = useTranslation()
  const { isLoading } = useAppSelector((state) => state.events)
  const event = useAppSelector((state) => state.events.currentEvent)
  const [activeTab, setActiveTab] = useState<TabId>("invitation")
  const isOpenEvent = event?.is_open_event === 1

  // The "Invitation" tab is where invite-only hosts manage outbound
  // invitations. For open events the same surface shows inbound registrations
  // instead, so we swap just the label — the data path and filters stay the
  // same, avoiding a parallel code path.
  const tabsForEvent = useMemo(
    () =>
      TABS.map((tab) =>
        tab.id === "invitation" && isOpenEvent
          ? { ...tab, label: t("eventDashboard.tabRegistrations") }
          : tab,
      ),
    [isOpenEvent, t],
  )

  const [invitationFilter, setInvitationFilter] = useState<StatusFilter>("all")
  const [reminderInvFilter, setReminderInvFilter] = useState<InvitationStatusFilter>("all")
  const [reminderStatusFilter, setReminderStatusFilter] = useState<ReminderStatusFilter>("all")
  const [rsvpInvFilter, setRsvpInvFilter] = useState<InvitationStatusFilter>("all")
  const [rsvpStatusFilter, setRsvpStatusFilter] = useState<ReminderStatusFilter>("all")
  const [qrInvFilter, setQrInvFilter] = useState<InvitationStatusFilter>("all")
  const [qrStatusFilter, setQrStatusFilter] = useState<ReminderStatusFilter>("all")
  const [thankInvFilter, setThankInvFilter] = useState<InvitationStatusFilter>("all")
  const [thankStatusFilter, setThankStatusFilter] = useState<ThankStatusFilter>("all")

  const [showAddGuests, setShowAddGuests] = useState(false)
  const [editShow, setEditShow] = useState(false)
  const [changeGuest, setChangeGuest] = useState<any>({})
  const [detailGuest, setDetailGuest] = useState<any>(null)
  const [showDetail, setShowDetail] = useState(false)
  const [validation, setValidation] = useState<any>([])
  const [checked, setChecked] = useState<any>([])

  const notify = (text: string, isError: boolean) => {
    isError
      ? toast.error(text, { position: "top-right", autoClose: 3000 })
      : toast.success(text, { position: "top-right", autoClose: 3000 })
  }

  const editPopupOpen = (guest: any) => {
    setChangeGuest(guest)
    setEditShow(true)
  }

  const handleRowClick = (guest: any) => {
    setDetailGuest(guest)
    setShowDetail(true)
  }

  const handleDetailToEdit = (guest: any) => {
    setShowDetail(false)
    setChangeGuest(guest)
    setEditShow(true)
  }

  const filter = {
    sort: "",
    order: "",
    searchValue: "",
    statusNumber: "",
    reminderNumber: "",
    id: Number(event_id),
  }

  const { lastRefreshed } = useBackgroundRefresh({
    eventId: event_id,
  })

  useEffect(() => {
    dispatch(getEvent(Number(event_id)))
  }, [dispatch, event_id])

  const handleTabClick = (tabId: TabId) => {
    setActiveTab(tabId)
  }

  if (isLoading || !event) {
    return (
      <div className={styles.page}>
        <div className={styles.pageContainer}>
          <div className={styles.cardEvent}>
            <div className={styles.skeletonBanner} />
            <div className={styles.skeletonHeader}>
              <div className={styles.skeletonHeaderLeft}>
                <div className={`${styles.skeleton} ${styles.skeletonTitle}`} />
                <div className={styles.skeletonMeta}>
                  <div className={`${styles.skeleton} ${styles.skeletonMetaItem}`} />
                  <div className={`${styles.skeleton} ${styles.skeletonMetaItem}`} />
                  <div className={`${styles.skeleton} ${styles.skeletonMetaItem}`} />
                </div>
              </div>
              <div className={styles.skeletonHeaderRight}>
                <div className={`${styles.skeleton} ${styles.skeletonIconBtn}`} />
                <div className={`${styles.skeleton} ${styles.skeletonIconBtn}`} />
                <div className={`${styles.skeleton} ${styles.skeletonIconBtn}`} />
                <div className={`${styles.skeleton} ${styles.skeletonAddBtn}`} />
              </div>
            </div>
            <div className={styles.skeletonTabs}>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={`${styles.skeleton} ${styles.skeletonTabItem}`} />
              ))}
            </div>
          </div>

          <div className={styles.card}>
            <div className={`${styles.skeleton} ${styles.skeletonSectionLabel}`} />
            <div className={styles.skeletonOverviewGrid}>
              {[1, 2, 3].map((i) => (
                <div key={i} className={styles.skeletonStatCard}>
                  <div className={`${styles.skeleton} ${styles.skeletonStatNumber}`} />
                  <div className={`${styles.skeleton} ${styles.skeletonStatLabel}`} />
                </div>
              ))}
            </div>
            <div className={styles.skeletonOverviewGrid} style={{ marginTop: 16 }}>
              {[1, 2, 3].map((i) => (
                <div key={i} className={styles.skeletonStatCard}>
                  <div className={`${styles.skeleton} ${styles.skeletonStatNumber}`} />
                  <div className={`${styles.skeleton} ${styles.skeletonStatLabel}`} />
                </div>
              ))}
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.skeletonTableHeader}>
              <div className={`${styles.skeleton} ${styles.skeletonSearchBar}`} />
              <div className={`${styles.skeleton} ${styles.skeletonSendBtn}`} />
            </div>
            <div className={styles.skeletonTableTabs}>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={`${styles.skeleton} ${styles.skeletonTabChip}`} />
              ))}
            </div>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={styles.skeletonTableRow}>
                <div className={`${styles.skeleton} ${styles.skeletonCell}`} style={{ width: 16, height: 16 }} />
                <div className={`${styles.skeleton} ${styles.skeletonCell}`} style={{ width: 40 }} />
                <div className={`${styles.skeleton} ${styles.skeletonCell}`} style={{ width: "25%", maxWidth: 160 }} />
                <div className={`${styles.skeleton} ${styles.skeletonCell}`} style={{ width: "20%", maxWidth: 140 }} />
                <div className={`${styles.skeleton} ${styles.skeletonCell}`} style={{ width: 80 }} />
                <div className={`${styles.skeleton} ${styles.skeletonCell}`} style={{ width: 64 }} />
                <div className={`${styles.skeleton} ${styles.skeletonCell}`} style={{ width: 32, height: 32, borderRadius: 3 }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page} dir={i18n.language === "ar" ? "rtl" : "ltr"}>
      <div className={styles.pageContainer}>
        <div className={styles.cardEvent}>
          <EventBanner event={event} eventId={event_id!} lastRefreshed={lastRefreshed} />
          <EventHeader event={event} onAddGuests={() => setShowAddGuests(true)} />

          <div className={styles.tabs}>
            {tabsForEvent.map((tab) => (
              <button
                key={tab.id}
                className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ""}`}
                onClick={() => handleTabClick(tab.id)}
              >
                {"label" in tab ? tab.label : t(`eventDashboardV2.tabs.${tab.labelKey}`)}
              </button>
            ))}
          </div>
        </div>

        {activeTab === "invitation" && (
          <>
            <div className={styles.card}>
              <Overview
                event={event}
                activeFilter={invitationFilter}
                onFilterChange={setInvitationFilter}
              />
            </div>
            <div className={styles.card}>
              <GuestList
                event={event}
                activeFilter={invitationFilter}
                onFilterChange={setInvitationFilter}
                editPopupOpen={editPopupOpen}
                onRowClick={handleRowClick}
                filter={filter}
                notify={notify}
                checked={checked}
                setChecked={setChecked}
              />
            </div>
          </>
        )}

        {activeTab === "reminders" && (
          <>
            <div className={styles.card}>
              <RemindersOverview
                invitationFilter={reminderInvFilter}
                reminderFilter={reminderStatusFilter}
                onInvitationFilterChange={setReminderInvFilter}
                onReminderFilterChange={setReminderStatusFilter}
                reminderType={0}
              />
            </div>
            <div className={styles.card}>
              <RemindersGuestList
                event={event}
                invitationFilter={reminderInvFilter}
                reminderFilter={reminderStatusFilter}
                onInvitationFilterChange={setReminderInvFilter}
                onReminderFilterChange={setReminderStatusFilter}
                editPopupOpen={editPopupOpen}
                filter={filter}
                notify={notify}
              />
            </div>
          </>
        )}

        {activeTab === "rsvp-reminder" && (
          <>
            <div className={styles.card}>
              <RemindersOverview
                invitationFilter={rsvpInvFilter}
                reminderFilter={rsvpStatusFilter}
                onInvitationFilterChange={setRsvpInvFilter}
                onReminderFilterChange={setRsvpStatusFilter}
                reminderType={1}
              />
            </div>
            <div className={styles.card}>
              <RemindersGuestList
                event={event}
                invitationFilter={rsvpInvFilter}
                reminderFilter={rsvpStatusFilter}
                onInvitationFilterChange={setRsvpInvFilter}
                onReminderFilterChange={setRsvpStatusFilter}
                editPopupOpen={editPopupOpen}
                filter={filter}
                notify={notify}
                reminderType={1}
              />
            </div>
          </>
        )}

        {activeTab === "qr-reminder" && (
          <>
            <div className={styles.card}>
              <RemindersOverview
                invitationFilter={qrInvFilter}
                reminderFilter={qrStatusFilter}
                onInvitationFilterChange={setQrInvFilter}
                onReminderFilterChange={setQrStatusFilter}
                reminderType={2}
              />
            </div>
            <div className={styles.card}>
              <RemindersGuestList
                event={event}
                invitationFilter={qrInvFilter}
                reminderFilter={qrStatusFilter}
                onInvitationFilterChange={setQrInvFilter}
                onReminderFilterChange={setQrStatusFilter}
                editPopupOpen={editPopupOpen}
                filter={filter}
                notify={notify}
                reminderType={2}
              />
            </div>
          </>
        )}

        {activeTab === "thank-you" && (
          <>
            <div className={styles.card}>
              <ThankYouOverview
                invitationFilter={thankInvFilter}
                thankFilter={thankStatusFilter}
                onInvitationFilterChange={setThankInvFilter}
                onThankFilterChange={setThankStatusFilter}
              />
            </div>
            <div className={styles.card}>
              <ThankYouGuestList
                event={event}
                invitationFilter={thankInvFilter}
                thankFilter={thankStatusFilter}
                onInvitationFilterChange={setThankInvFilter}
                onThankFilterChange={setThankStatusFilter}
                editPopupOpen={editPopupOpen}
                filter={filter}
                notify={notify}
              />
            </div>
          </>
        )}

        <AddGuestsDrawer
          show={showAddGuests}
          onClose={() => setShowAddGuests(false)}
          filter={filter}
          notify={notify}
        />

        <GuestDetailDrawer
          guest={detailGuest}
          show={showDetail}
          setShow={setShowDetail}
          onEdit={handleDetailToEdit}
        />

        <EditGuestDrawer
          guest={changeGuest}
          setGuest={setChangeGuest}
          show={editShow}
          setShow={setEditShow}
          validation={validation}
          setValidation={setValidation}
          filter={filter}
          event_id={event_id}
          notify={notify}
        />
      </div>
    </div>
  )
}

export default EventDashboardV2
